import type { ChatClient } from '@alternativamais/chat-core';
import { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router';

import { lerPreferencia } from './armazenamento/local';
import { Avisos } from './componentes/Avisos';
import { ProtecaoDeErro } from './componentes/ProtecaoDeErro';
import { ProvedorChat, useEstado, useSessao } from './chat/contexto';
import { BarraDeTitulo } from './desktop/BarraDeTitulo';
import { useAplicarPreferencias } from './chat/preferencias';
import { Conectando } from './telas/acesso/Conectando';
import { Entrar } from './telas/acesso/Entrar';
import { PrimeiroAcesso } from './telas/acesso/PrimeiroAcesso';
import { Aplicativo } from './telas/chat/Aplicativo';

export function App() {
  return (
    <ProtecaoDeErro>
      <BrowserRouter>
        <Conteudo />
      </BrowserRouter>
    </ProtecaoDeErro>
  );
}

/** O app sem o roteador (os testes usam um `MemoryRouter` e um cliente falso). */
export function Conteudo({
  fabrica,
}: {
  fabrica?: (aoExpirar: () => void) => ChatClient;
}) {
  return (
    <ProvedorChat fabrica={fabrica}>
      {/* No app para computador, a barra de título fica acima de tudo (E2). */}
      <div className="flex h-full flex-col">
        <BarraDeTitulo />
        <div className="relative min-h-0 flex-1">
          <Raiz />
        </div>
      </div>
      <Avisos />
    </ProvedorChat>
  );
}

/**
 * Link de primeiro acesso: a tela dele, com ou sem login. Sem login: entrar;
 * senha inicial: trocar (A2); abrindo: conectando; pronto: o chat.
 */
function Raiz() {
  const { sessao } = useSessao();
  const local = useLocation();
  const primeiroAcesso = /^\/primeiro-acesso\/([^/]+)/.exec(local.pathname);
  if (primeiroAcesso) {
    return (
      <PrimeiroAcesso
        key={primeiroAcesso[1]}
        modo="link"
        token={decodeURIComponent(primeiroAcesso[1])}
      />
    );
  }
  if (sessao === 'deslogado') return <Entrar />;
  if (sessao === 'trocar-senha') return <PrimeiroAcesso modo="trocar" />;
  if (sessao !== 'pronto') return <Conectando />;
  return <Rotas />;
}

function Rotas() {
  useTema();
  useAplicarPreferencias();
  return (
    <Routes>
      <Route path="/canais/:servidor" element={<Aplicativo />} />
      <Route path="/canais/:servidor/:canal" element={<Aplicativo />} />
      <Route
        path="/canais/:servidor/:canal/:mensagem"
        element={<Aplicativo />}
      />
      <Route
        path="/canais/:servidor/:canal/threads/:thread"
        element={<Aplicativo />}
      />
      <Route path="/convite/:codigo" element={<ConviteRecebido />} />
      <Route path="*" element={<Navigate to={rotaInicial()} replace />} />
    </Routes>
  );
}

/** Link de convite (`/convite/:codigo`): abre o início com o convite na tela (N6). */
function ConviteRecebido() {
  const { codigo } = useParams();
  return (
    <Navigate
      to={`/canais/@me?convite=${encodeURIComponent(codigo ?? '')}`}
      replace
    />
  );
}

function rotaInicial(): string {
  const ultima = lerPreferencia<string | null>('ultima-rota', null);
  return ultima?.startsWith('/canais/') ? ultima : '/canais/@me';
}

/** Tema das configurações da conta (escuro, claro ou o do sistema). */
function useTema() {
  const tema = useEstado((estado) => estado.userSettings?.theme ?? 'dark');
  useEffect(() => {
    const raiz = document.documentElement;
    const aplicar = () => {
      const claro =
        tema === 'light' ||
        (tema === 'system' &&
          window.matchMedia('(prefers-color-scheme: light)').matches);
      raiz.dataset.tema = claro ? 'claro' : 'escuro';
    };
    aplicar();
    if (tema !== 'system') return;
    const consulta = window.matchMedia('(prefers-color-scheme: light)');
    consulta.addEventListener('change', aplicar);
    return () => consulta.removeEventListener('change', aplicar);
  }, [tema]);
}
